import React, { useMemo, useRef, useState } from 'react';
import { useRecoilStateLoadable } from 'recoil';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from '../../components/modals/ModalWithStyles';
import {
  DataTable,
  IColDef,
} from '../../modules/Manage/pages/queues-table/DataTable';
import { GridSortModel } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

/**
 * Queue editing component
 *
 * @param {Object} props
 * @param {string} [props.id] Queue id
 * @param {boolean} props.show True if modal shown
 * @param {onHideCallback} props.onHide Function to hide modal
 *
 * @see https://github.com/microsoft/vscode/issues/41296
 */
export function BusinessSMSNumbersDialog(props: {
  id?: string;
  show: boolean;
  onHide: () => void;
  setUpdatedRowId?: (rowId: string | undefined) => void;
}) {
  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);

  const userAtomContents = userAtomLoadable?.contents as IUserAtom;

  type TNumbers = {
    numberId: number;
    DID: string;
    businessId: string;
  };

  // const businessSMSRule = useMemo(() => {
  //   return props.rules.find((rule) => rule.businessSMSId === props.id);
  // }, [props.rules, props.id]);
  const [_, businessSMSLoadable] = makeApiRequestCached(
    {
      method: 'get',
      // stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.BUSINESS_SMS_BY_BUSINESS +
        `/${userAtomContents?.businessid}` +
        `/${props.id}`,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const businessSMSInboxCached = useRef({} as any);
  businessSMSInboxCached.current = useMemo(
    () =>
      businessSMSLoadable.state === 'hasValue'
        ? {
            id:
              businessSMSLoadable.contents?.response?.data?.data?.businessSMSId,
            ...businessSMSLoadable.contents?.response?.data?.data,
          }
        : businessSMSInboxCached.current,
    [businessSMSLoadable, userAtomContents]
  );

  const businessSMSNumbersCached =
    businessSMSInboxCached?.current?.numbers?.map((team: TNumbers) => {
      return {
        id: team.numberId,
        ...team,
      };
    }) ?? [];

  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Number ID',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'DID',
        headerName: 'Phone Number',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'businessId',
        headerName: 'Business ID',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
    ],
    [businessSMSNumbersCached.current, userAtomContents?.user?.role]
  );

  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);

  return (
    <ModalWithStyles
      title={'Team SMS Numbers'}
      show={props.show}
      onHide={props.onHide}
      noPaddingDialog={true}
    >
      <DataTable
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
        rows={businessSMSNumbersCached}
        columns={columns}
        loading={businessSMSLoadable.state === 'loading'}
      />
    </ModalWithStyles>
  );
}
